import React, { ReactNode } from 'react';
import { InformationCircleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import PermissionConfigurable from '../PermissionConfigurable';

interface StatCardProps {
  title: string;
  value: string | number;
  secondaryValue?: string | number;
  secondaryValueColor?: 'green' | 'red' | 'gray' | 'yellow';
  showLoadTime?: boolean;
  loadTime?: number | null;
  loading?: boolean;
  showInfoButton?: boolean;
  showViewButton?: boolean;
  onInfoClick?: () => void;
  onViewClick?: () => void;
  children?: ReactNode; // For custom content like the ELD device breakdown
  componentId?: string; // For permissions system
  headerContent?: ReactNode; // For custom content in the header area (top right)
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  secondaryValue,
  secondaryValueColor = 'gray',
  showLoadTime = false,
  loadTime = null,
  loading = false,
  showInfoButton = true,
  showViewButton = true,
  onInfoClick,
  onViewClick,
  children,
  componentId,
  headerContent,
}) => {
  // Generate default componentId from title if not provided
  const finalComponentId = componentId || `statcard-${title.toLowerCase().replace(/\s+/g, '-')}`;

  const getSecondaryValueColorClass = () => {
    switch (secondaryValueColor) {
      case 'green':
        return 'text-green-600 dark:text-green-400';
      case 'red':
        return 'text-red-600 dark:text-red-400';
      case 'yellow':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'gray':
      default:
        return 'text-gray-500 dark:text-gray-500';
    }
  };

  const cardContent = (
    <div className="bg-panel rounded-lg shadow p-6 relative h-full">
      {/* Load Time Display */}
      {showLoadTime && loadTime !== null && !headerContent && (
        <div className="absolute top-4 right-4 text-xs text-gray-400">
          {(loadTime / 1000).toFixed(2)}s
        </div>
      )}

      {/* Header Content (top right) */}
      {headerContent && (
        <div className="absolute top-4 right-4">
          {headerContent}
        </div>
      )}

      {/* Main Content */}
      <div>
        <p className="text-sm text-gray-600 dark:text-gray-400">{title}</p>
        {loading ? (
          <>
            <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mt-2"></div>
            {secondaryValue !== undefined && (
              <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mt-2"></div>
            )}
            {children && (
              <div className="mt-2 space-y-1">
                <div className="h-3 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-3 w-36 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>
            )}
          </>
        ) : (
          <>
            <p className="text-2xl font-bold text-heading">
              {value}
            </p>
            {secondaryValue !== undefined && (
              <p className={`text-sm ${getSecondaryValueColorClass()}`}>
                {secondaryValue}
              </p>
            )}
            {children}
          </>
        )}
      </div>

      {/* Action Buttons */}
      <div className="absolute bottom-4 right-4 flex items-center gap-2">
        {showInfoButton && (
          <button
            onClick={onInfoClick}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            disabled={loading}
          >
            <InformationCircleIcon className="w-6 h-6" />
          </button>
        )}
        {showViewButton && (
          <button
            onClick={onViewClick}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            disabled={loading}
          >
            <MagnifyingGlassIcon className="w-6 h-6" />
          </button>
        )}
      </div>
    </div>
  );

  // Always wrap with PermissionConfigurable (using auto-generated or provided componentId)
  return (
    <PermissionConfigurable componentId={finalComponentId} componentName={title}>
      {cardContent}
    </PermissionConfigurable>
  );
};

export default StatCard;
